import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'May 19, 2026';
const CONTACT_EMAIL = 'extractstudy@proton.me';

const Terms = () => {
  useEffect(() => {
    document.title = 'Terms of Service | ExtractStudy';
  }, []);

  return (
    <div className="min-h-screen app-shell px-4 py-10 transition-colors duration-300">
      <div className="pointer-events-none fixed inset-0 opacity-65 mix-blend-screen" style={{ background: 'radial-gradient(60% 50% at 20% 30%, rgba(139,208,255,0.10), transparent), radial-gradient(45% 40% at 80% 20%, rgba(74,163,255,0.08), transparent)' }}></div>

      <div className="relative mx-auto max-w-2xl">
        <Link to="/" className="text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors">
          ← ExtractStudy
        </Link>

        <h1 className="section-title brand-title text-3xl sm:text-4xl font-bold mt-4 mb-2">
          Terms of Service
        </h1>
        <p className="text-[var(--muted)] text-sm mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="glass-panel rounded-2xl border border-[var(--border)] p-6 sm:p-8 space-y-6 text-[var(--text)] text-sm leading-relaxed">
          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">1. Acceptance</h2>
            <p>
              These Terms of Service ("Terms") govern your use of ExtractStudy ("the
              service", "we", "us"), available at <a href="https://www.extractstudy.com" className="text-[var(--accent)] hover:underline">www.extractstudy.com</a>.
              By using the service you agree to be bound by these Terms. If you do not
              agree, do not use the service.
            </p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">2. The Service</h2>
            <p>ExtractStudy is an AI-powered vocabulary flashcard generator. You submit a
            passage of text or a photo containing text; we use third-party AI models to
            extract vocabulary, translate it, and return study-ready flashcards. Logged-in
            users may save flashcards to a personal Vocabulary Book.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">3. Accounts</h2>
            <p>You may use ExtractStudy without an account for ad-hoc generations. To save
            words to a Vocabulary Book you must create an account using a valid email
            address (or sign in with Google). You are responsible for keeping your password
            confidential and for activity that occurs under your account.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">4. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Upload content you do not have the right to use, or content that is illegal,
              hateful, harassing, or infringes the rights of others.</li>
              <li>Attempt to abuse, overload, or disrupt the service, or bypass rate limits.</li>
              <li>Use the service to generate or train competing AI systems.</li>
              <li>Scrape, mirror, or resell the service without our written permission.</li>
            </ul>
            <p className="mt-2">We may suspend or terminate accounts that violate these rules.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">5. Rate Limits</h2>
            <p>To keep the service available to everyone, free use is capped at a small
            number of generations per minute and per day. The specific numbers may change
            without notice.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">6. Content You Submit</h2>
            <p>You keep ownership of the text and images you submit. By submitting content,
            you grant us a limited, non-exclusive license to process it for the sole purpose
            of generating flashcards and delivering them back to you. Submitted content is
            not retained after processing; saved flashcards remain in your Vocabulary Book
            until you delete them.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">7. AI Output Disclaimer</h2>
            <p>Flashcards and translations are produced by AI models and may contain errors.
            ExtractStudy is provided as a study aid, not as a source of authoritative
            translation. You are responsible for verifying anything important before
            relying on it (e.g., professional, legal, or medical contexts).</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">8. Availability & Changes</h2>
            <p>The service is provided on an "as is" and "as available" basis. We do not
            guarantee uptime, and we may add, change, or remove features at any time
            without notice.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">9. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law, ExtractStudy, its operators, and its
            third-party providers are not liable for indirect, incidental, special,
            consequential, or punitive damages arising from your use of the service. Our
            aggregate liability for any claim relating to the service is limited to the
            greater of (a) the amount you paid us in the past twelve months (which for free
            use is $0) or (b) USD $50.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">10. Termination</h2>
            <p>You may stop using the service at any time and request account deletion by
            emailing us. We may suspend or terminate access if you violate these Terms.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">11. Governing Law</h2>
            <p>These Terms are governed by the laws of the jurisdiction in which the service
            operator is established, without regard to conflict-of-laws principles.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">12. Changes to These Terms</h2>
            <p>We may update these Terms. Material changes will be reflected in the
            "Last updated" date above. Continued use after a change constitutes acceptance
            of the updated Terms.</p>
          </section>

          <section>
            <h2 className="text-[var(--accent)] font-semibold text-base mb-2">13. Contact</h2>
            <p>Questions about these Terms can be sent to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--accent)] hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </section>
        </div>

        <div className="mt-6 text-center">
          <Link to="/privacy" className="text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors mr-4">
            Privacy Policy
          </Link>
          <Link to="/" className="text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors">
            Back to ExtractStudy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Terms;
